import {
  type BranchKey,
  STANDARD_BRANCH,
  useCopyBranch,
} from '@festina-life/api'
import { ErrorMessage } from '@festina-life/components'
import {
  assertNotEmpty,
  notEmpty,
  type Optional,
  typeUtils,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Alert, toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikTextInput } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack, useClose } from '@festina-life/ui/structure'
import { useState } from 'react'
import * as Yup from 'yup'

import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { BranchNameAndCategory } from '../../../branchNameWithStatus/BranchNameAndCategory'

type CopyBranchInput = {
  newBranchName: string
}

const validate = typeUtils.createValidator<CopyBranchInput>()

type Props = {
  branchKey: BranchKey
}

export const CopyBranchForm = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const { mutateAsync: copyBranch, isPending } = useCopyBranch()
  const [error, setError] = useState<Optional<Error>>()
  const close = useClose()
  const allConciseBranches = useAllConciseBranches()
  const sourceBranch = useConciseBranch({
    branchName: branchKey.name,
    server: branchKey.server,
    shouldFallbackToStandard: false,
  })

  assertNotEmpty(sourceBranch)

  const serverBranches = allConciseBranches[branchKey.server] ?? []

  const existingBranchNames = serverBranches
    .map(({ name }) => name)
    .filter(notEmpty)

  const dependentBranches = serverBranches.filter(
    ({ name, baseBranchName, followsBranchName }) =>
      name !== branchKey.name &&
      (baseBranchName === branchKey.name ||
        followsBranchName === branchKey.name),
  )

  const validationSchema = Yup.object({
    newBranchName: Yup.string()
      .required(translate('validation.required'))
      .noSpaces(translate('validation.noSpaces'))
      .noSeparatorIssues(translate('validation.noSeparatorIssues'))
      .notForbidden(translate('validation.notForbidden'), [STANDARD_BRANCH])
      .notDuplicated(
        translate('branchCreationForm.validation.branchName.duplicatedName'),
        existingBranchNames,
      ),
  })

  const handleSubmit = async ({ newBranchName }: CopyBranchInput) => {
    setError(undefined)
    assertNotEmpty(sourceBranch.name)
    assertNotEmpty(sourceBranch.branchCategory)

    try {
      await copyBranch({
        server: branchKey.server,
        newBranchName,
        sourceBranch,
      })

      toast({
        variant: 'success',
        title: translate('branchDetailsPanel.tabs.manage.copyBranch.success', {
          branchName: branchKey.name,
          newBranchName,
        }),
      })
      close()
    } catch (error) {
      setError(error)
    }
  }

  return (
    <FormikForm
      initialValues={{ newBranchName: '' }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {dependentBranches.length > 0 && (
        <Alert
          severity="info"
          title={translate(
            'branchDetailsPanel.tabs.manage.copyBranch.dependentBranchesWarning',
          )}
        >
          <Stack className="flex-wrap mt-2" gap="spacious">
            {dependentBranches.map((branch) => (
              <BranchNameAndCategory key={branch.name} specification={branch} />
            ))}
          </Stack>
        </Alert>
      )}

      <FormikTextInput
        name={validate('newBranchName')}
        label={translate(
          'branchDetailsPanel.tabs.manage.copyBranch.newNameLabel',
        )}
      />

      {error && <ErrorMessage error={error} />}

      <Stack className="w-full justify-end">
        <Button type="submit" variant="primary" isLoading={isPending}>
          {translate('branchDetailsPanel.tabs.manage.copyBranch')}
        </Button>
      </Stack>
    </FormikForm>
  )
}
